import {
  type CyclesPhase,
  type CyclesSnapshot,
  isCpu,
  MAX_CYCLES,
  MIN_CYCLES,
  REACTION_SETS,
} from '@xox/shared'
import { ArrowLeft, LogOut, Play, RotateCcw } from 'lucide-react'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { BoardOverlay } from '../components/BoardOverlay'
import { InvitePanel } from '../components/InvitePanel'
import { ReactionBar, ReactionOverlay } from '../components/Reactions'
import { isRoomGone, JoiningNotice, RoomChrome, RoomClosedCard } from '../components/RoomShell'
import { CountdownOverlay } from '../components/race/CountdownOverlay'
import { Lineup, type LineupEntry } from '../components/race/Lineup'
import { PartyLobby } from '../components/race/PartyLobby'
import { type PodiumEntry, RoundResults } from '../components/race/RoundResults'
import { type StandingRow, Standings } from '../components/race/Standings'
import { hostNameOf, WaitingPill } from '../components/race/WaitingPill'
import { Button, Card, IconButton, IconLink } from '../components/ui'
import { useAutoSeat } from '../hooks/useAutoSeat'
import { useConnectRoom } from '../hooks/useConnectRoom'
import { useCountdown } from '../hooks/useCountdown'
import { ordinal, plural } from '../lib/format'
import { cyclesServerNow, useCycles } from '../store/cycles'
import { useSession } from '../store/session'
import { Cycle } from './art/Cycle'
import { ControlSchemePicker } from './ControlSchemePicker'
import { CyclesArena } from './CyclesArena'
import { CyclesSettingsFields } from './CyclesSettingsFields'
import { type ControlScheme, controlScheme, useCyclesInput } from './useCyclesInput'

function entriesOf(snap: CyclesSnapshot, me: string | null): LineupEntry[] {
  return snap.racers.map((r) => ({
    id: r.profile.id,
    nickname: r.profile.nickname,
    color: r.color,
    isHost: r.profile.id === snap.hostId,
    isMe: r.profile.id === me,
    cpu: r.cpu,
  }))
}

const cycleFigure = (e: LineupEntry) => <Cycle color={e.color} className="w-12 drop-shadow" />
const podiumFigure = (e: LineupEntry, place: number) => (
  <Cycle color={e.color} className={place === 1 ? 'w-16' : 'w-12'} />
)

export function CyclesRoom() {
  const { code = '' } = useParams()
  const navigate = useNavigate()
  const me = useSession((s) => s.me)
  const room = useCycles()
  const [scheme, setScheme] = useState<ControlScheme>(() => controlScheme.load())
  // "Look at the board" hides the result card until the phase changes.
  const [peeked, setPeeked] = useState<CyclesPhase | null>(null)
  useConnectRoom(code, me?.playerId, useCycles)

  const snap = room.snapshot
  const phase = snap?.phase
  const peek = peeked !== null && peeked === phase
  const mineRacer = snap?.racers.find((r) => r.profile.id === room.playerId) ?? null
  useAutoSeat(snap !== null, mineRacer !== null, room.takeSeat)
  const myCycle = room.live?.cycles.find((c) => c.id === room.playerId) ?? null
  const isHost = !!snap && snap.hostId === room.playerId
  const steering =
    (phase === 'racing' || phase === 'countdown') && !!myCycle?.alive && room.status === 'open'
  const input = useCyclesInput(steering, scheme)
  const nextRoundIn = useCountdown(
    phase === 'roundEnd' ? (snap?.deadlineAt ?? null) : null,
    cyclesServerNow,
  )

  if (me && isRoomGone(room)) {
    return (
      <RoomClosedCard
        closedReason={room.closedReason === 'closed' ? 'closed' : 'expired'}
        noun="room"
        onBack={() => navigate('/cycles')}
        backLabel="Back to Light Cycles"
      />
    )
  }
  if (!snap) return <JoiningNotice label={`Joining arena ${code}…`} />

  const entries = entriesOf(snap, room.playerId)
  const byId = new Map(entries.map((e) => [e.id, e]))
  const round = snap.round
  const rounds = snap.settings.rounds
  const pickScheme = (s: ControlScheme) => {
    setScheme(s)
    controlScheme.save(s)
  }

  const leave = () => {
    room.leave()
    navigate('/cycles')
  }

  const title =
    snap.phase === 'lobby'
      ? 'Lobby'
      : snap.phase === 'countdown'
        ? 'Get ready…'
        : snap.phase === 'results'
          ? 'Match results'
          : `Round ${round?.no ?? 1} of ${rounds}`

  // Standings: by score, then alive before crashed, then by place in the last round.
  const place = (id: string) => snap.lastRound?.placeById[id]
  const rows: StandingRow[] = [...snap.racers]
    .sort((a, b) => (snap.scores[b.profile.id] ?? 0) - (snap.scores[a.profile.id] ?? 0))
    .map((r) => {
      const c = room.live?.cycles.find((x) => x.id === r.profile.id)
      const p = place(r.profile.id)
      return {
        id: r.profile.id,
        nickname: r.profile.nickname,
        isHost: r.profile.id === snap.hostId,
        online: r.online,
        status:
          snap.phase === 'racing' && c ? (
            c.alive ? (
              <span className="font-bold text-emerald-600">riding</span>
            ) : (
              'crashed'
            )
          ) : p !== undefined && snap.phase !== 'lobby' ? (
            ordinal(p)
          ) : (
            ''
          ),
        score: snap.scores[r.profile.id] ?? 0,
      }
    })
  const standingFigure = (row: StandingRow) => (
    <Cycle color={byId.get(row.id)?.color ?? 'sky'} className="w-9" />
  )

  const podium: PodiumEntry[] =
    snap.phase === 'results'
      ? rows.slice(0, 3).map((r, i) => ({
          place: i + 1,
          entry: byId.get(r.id)!,
          detail: `${r.score} pts`,
        }))
      : Object.entries(snap.lastRound?.placeById ?? {})
          .filter(([id]) => byId.has(id))
          .map(([id, p]) => ({
            place: p,
            entry: byId.get(id)!,
            detail: `+${snap.lastRound?.pointsById[id] ?? 0} · ${snap.scores[id] ?? 0} pts`,
          }))
  const roundWinner = podium.find((p) => p.place === 1)
  const myPlace = room.playerId ? place(room.playerId) : undefined
  const matchTop = rows[0]

  return (
    <div className="flex flex-1 flex-col gap-4 lg:flex-row lg:items-start lg:gap-8">
      <RoomChrome error={room.error} onDismiss={room.dismissError} status={room.status} />

      <section className="flex flex-1 flex-col items-center gap-3" aria-label="Arena">
        <div className="flex w-full max-w-[420px] items-center justify-between gap-2 text-sm">
          <span className="min-w-0 truncate font-semibold">{title}</span>
          <span className="flex shrink-0 items-center gap-1 text-slate-500">
            {snap.racers.length}/{MAX_CYCLES}
            {snap.spectators > 0 && ` · ${snap.spectators} 👀`}
            {mineRacer ? (
              <IconButton label={isHost ? 'End the match for everyone' : 'Leave'} onClick={leave}>
                <LogOut className="size-4" aria-hidden />
              </IconButton>
            ) : (
              <IconLink to="/cycles" label="Back to Light Cycles">
                <ArrowLeft className="size-4" aria-hidden />
              </IconLink>
            )}
          </span>
        </div>

        <div className="relative w-full" style={{ maxWidth: 'min(420px, 60dvh * 5 / 7)' }}>
          <CyclesArena
            className={snap.phase === 'lobby' ? 'opacity-70' : ''}
            {...(steering ? input : {})}
          />
          {snap.phase === 'lobby' && (
            <BoardOverlay wide shape="square">
              <PartyLobby
                eyebrow={`Best of ${rounds} rounds`}
                title={
                  snap.racers.length < MIN_CYCLES && snap.settings.bots === 0
                    ? 'Waiting for riders'
                    : 'Ready to ride?'
                }
                entries={entries}
                min={snap.settings.bots > 0 ? 1 : MIN_CYCLES}
                max={MAX_CYCLES}
                figure={cycleFigure}
                hostId={snap.hostId}
                playerId={room.playerId}
                onStart={room.start}
                startLabel="Start match"
                onJoin={room.takeSeat}
                joinLabel="Join the match"
                fullLabel="Arena is full — you are watching"
                seatNoun="rider"
                invite={{ code, path: '/cycles/r', game: 'cycles' }}
              >
                <div className="w-full rounded-2xl bg-slate-100 p-3 dark:bg-slate-800">
                  <CyclesSettingsFields
                    value={snap.settings}
                    onChange={room.settings}
                    disabled={!isHost}
                  />
                  {!isHost && (
                    <p className="mt-1 text-center text-[11px] text-slate-500">
                      {hostNameOf(entries, snap.hostId)} picks the rules
                    </p>
                  )}
                </div>
              </PartyLobby>
            </BoardOverlay>
          )}
          {snap.phase === 'countdown' && (
            <CountdownOverlay
              deadlineAt={snap.deadlineAt}
              serverNow={cyclesServerNow}
              shape="square"
            >
              <Lineup
                entries={entries}
                min={MIN_CYCLES}
                max={MAX_CYCLES}
                figure={cycleFigure}
                onDark
                label="Riders"
              />
            </CountdownOverlay>
          )}
          {snap.phase === 'racing' && mineRacer && myCycle && !myCycle.alive && (
            <div className="pointer-events-none absolute inset-x-0 top-3 z-10 flex justify-center">
              <span className="rounded-full bg-slate-900/80 px-3 py-1 text-sm font-bold text-white shadow">
                Crashed — {plural(room.live?.cycles.filter((c) => c.alive).length ?? 0, 'rider')}{' '}
                still riding
              </span>
            </div>
          )}
          {snap.phase === 'roundEnd' && !peek && (
            <BoardOverlay wide shape="square" onPeek={() => setPeeked('roundEnd')}>
              <RoundResults
                eyebrow={`Round ${round?.no ?? 1} of ${rounds}`}
                title={
                  myPlace === 1
                    ? 'You survived the round!'
                    : roundWinner
                      ? `${roundWinner.entry.nickname} survives`
                      : 'Everybody crashed'
                }
                subtitle={
                  myPlace !== undefined && myPlace !== 1 ? `You finished ${ordinal(myPlace)}` : null
                }
                podium={podium}
                figure={podiumFigure}
                confetti={myPlace === 1}
                footer={
                  <WaitingPill>
                    {nextRoundIn === null
                      ? 'Next round…'
                      : `Next round in ${Math.max(1, Math.ceil(nextRoundIn / 1000))}…`}
                  </WaitingPill>
                }
              />
            </BoardOverlay>
          )}
          {snap.phase === 'results' && !peek && (
            <BoardOverlay wide shape="square" onPeek={() => setPeeked('results')}>
              <RoundResults
                eyebrow="Match over"
                title={
                  matchTop?.id === room.playerId
                    ? 'You win the match!'
                    : `${matchTop?.nickname ?? 'Nobody'} wins the match`
                }
                subtitle={
                  mineRacer && matchTop?.id !== room.playerId
                    ? `You finished with ${plural(snap.scores[mineRacer.profile.id] ?? 0, 'point')}`
                    : null
                }
                podium={podium}
                figure={podiumFigure}
                confetti
                footer={
                  isHost ? (
                    <div className="flex w-full flex-col gap-2">
                      <Button variant="arcade" tone="go" className="w-full" onClick={room.start}>
                        <RotateCcw className="size-5" aria-hidden /> Play again
                      </Button>
                      <Button variant="ghost" onClick={room.next}>
                        <Play className="size-4" aria-hidden /> Back to the lobby
                      </Button>
                    </div>
                  ) : (
                    <WaitingPill>
                      {`Waiting for ${hostNameOf(entries, snap.hostId)} to start another match…`}
                    </WaitingPill>
                  )
                }
              />
            </BoardOverlay>
          )}
          <ReactionOverlay reactions={room.reactions} lanes="sender" />
        </div>

        <p className="text-center text-sm text-slate-500" aria-live="polite">
          {snap.phase === 'racing' && steering
            ? scheme === 'tap'
              ? 'Tap the left or right half to turn · arrow keys on a keyboard'
              : 'Swipe to steer · arrow keys on a keyboard'
            : snap.phase === 'racing' && mineRacer
              ? 'You crashed — watch the rest of the round'
              : snap.phase === 'racing'
                ? 'You are watching'
                : snap.phase === 'countdown' && steering
                  ? 'Queue your first turn now'
                  : ''}
        </p>
        {mineRacer && (
          <ControlSchemePicker value={scheme} onChange={pickScheme} className="lg:hidden" />
        )}
        <ReactionBar set={REACTION_SETS.xox} onReact={room.react} />
        {snap.phase !== 'lobby' && (
          <div className="w-full max-w-[400px] lg:hidden">
            <Standings rows={rows} me={room.playerId} layout="strip" figure={standingFigure} />
          </div>
        )}
      </section>

      <aside className="hidden w-80 shrink-0 flex-col gap-3 lg:flex" aria-label="Riders">
        <Card className="flex flex-col gap-3">
          <h2 className="font-semibold">Riders</h2>
          <Standings rows={rows} me={room.playerId} layout="list" figure={standingFigure} />
          {snap.racers.some((r) => isCpu(r.profile.id)) && (
            <p className="text-xs text-slate-500">GRID bots fill the empty slots.</p>
          )}
        </Card>
        <Card className="flex flex-col gap-3">
          <h2 className="font-semibold">Share this arena</h2>
          <InvitePanel code={code} path="/cycles/r" game="cycles" />
        </Card>
        {mineRacer && (
          <Card className="flex flex-col gap-2">
            <h2 className="font-semibold">Controls</h2>
            <ControlSchemePicker value={scheme} onChange={pickScheme} />
            <p className="text-xs text-slate-500">Arrow keys and WASD always work.</p>
          </Card>
        )}
      </aside>
    </div>
  )
}
